import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { 
  Truck, Package, MapPin, Clock, ChevronRight, LogOut, User,
  Loader2, RefreshCw, Navigation, Phone, CheckCircle, AlertTriangle,
  Receipt, FileText, DollarSign, Bell
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';
import { useDriverNotifications } from '../../context/DriverNotificationContext';
import SOSButton from '../../components/SOSButton';

const DriverDashboard = () => {
  const navigate = useNavigate();
  const [driver, setDriver] = useState(null);
  const [loads, setLoads] = useState([]);
  const [loading, setLoading] = useState(true);
  const { unreadCount, subscribeToPush, pushEnabled, isPushSupported } = useDriverNotifications();

  useEffect(() => {
    const token = localStorage.getItem('driver_token');
    if (!token) {
      navigate('/driver/login');
      return;
    }
    
    // Set auth header for driver requests
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    
    fetchData();
  }, [navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [profileRes, loadsRes] = await Promise.all([
        api.get('/driver/me'),
        api.get('/driver/loads')
      ]);
      
      setDriver(profileRes.data);
      setLoads(loadsRes.data);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem('driver_token');
        localStorage.removeItem('driver_info');
        navigate('/driver/login');
      } else {
        toast.error('Failed to load data');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('driver_token');
    localStorage.removeItem('driver_info');
    delete api.defaults.headers.common['Authorization'];
    navigate('/driver/login');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'assigned': return 'bg-yellow-500/20 text-yellow-400';
      case 'picked_up': return 'bg-blue-500/20 text-blue-400';
      case 'in_transit': return 'bg-purple-500/20 text-purple-400';
      case 'delivered': return 'bg-green-500/20 text-green-400';
      default: return 'bg-gray-500/20 text-gray-400';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'assigned': return 'Ready for Pickup';
      case 'picked_up': return 'Picked Up';
      case 'in_transit': return 'In Transit';
      case 'delivered': return 'Delivered';
      default: return status;
    }
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const activeLoads = loads.filter(l => l.assignment_status !== 'delivered');
  const completedLoads = loads.filter(l => l.assignment_status === 'delivered');

  if (loading) {
    return (
      <div className="min-h-screen bg-[#071423] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#4ADE80]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#071423] pb-20">
      {/* Header */}
      <div className="bg-[#0D1B2A] border-b border-[#3B82F6]/20 px-4 py-3 sticky top-0 z-10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#4ADE80] to-[#22C55E] flex items-center justify-center">
              <Truck className="w-5 h-5 text-black" />
            </div>
            <div>
              <h1 className="font-bold text-white">
                {driver?.first_name} {driver?.last_name}
              </h1>
              <p className="text-xs text-gray-400">{driver?.company_name || 'Driver'}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate('/driver/notifications')}
              className="relative"
            >
              <Bell className="w-4 h-4" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full text-[10px] flex items-center justify-center text-white font-bold">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </Button>
            <Button variant="ghost" size="sm" onClick={fetchData}>
              <RefreshCw className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={handleLogout}>
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Push Notification Prompt */}
      {isPushSupported && !pushEnabled && (
        <div className="px-4 pb-4">
          <Card className="border-[#4ADE80]/30 bg-[#4ADE80]/5">
            <CardContent className="p-3">
              <div className="flex items-center gap-3">
                <Bell className="w-8 h-8 text-[#4ADE80]" />
                <div className="flex-1">
                  <p className="text-white text-sm font-medium">Enable Notifications</p>
                  <p className="text-xs text-gray-400">Get notified when you receive new loads</p>
                </div>
                <Button 
                  size="sm" 
                  className="bg-[#4ADE80] hover:bg-[#22C55E] text-black text-xs"
                  onClick={async () => {
                    const success = await subscribeToPush();
                    if (success) {
                      toast.success('Notifications enabled!');
                    } else {
                      toast.error('Please allow notifications in your browser settings');
                    }
                  }}
                >
                  Enable
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Status Card */}
      <div className="px-4 py-4">
        <Card className="border-[#3B82F6]/20 bg-gradient-to-r from-[#0D1B2A] to-[#112240]">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-400 uppercase">Your Status</p>
                <p className={`text-lg font-bold capitalize ${
                  driver?.status === 'available' ? 'text-green-400' :
                  driver?.status === 'on_load' ? 'text-blue-400' : 'text-gray-400'
                }`}>
                  {driver?.status?.replace('_', ' ') || 'Available'}
                </p>
              </div>
              <div className="text-right">
                <p className="text-xs text-gray-400">Total Loads</p>
                <p className="text-2xl font-bold text-white">{driver?.total_loads || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Active Loads Section */}
      <div className="px-4">
        <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-3 flex items-center gap-2">
          <Package className="w-4 h-4" />
          Active Loads ({activeLoads.length})
        </h2>
        
        {activeLoads.length === 0 ? (
          <Card className="border-[#3B82F6]/20 bg-[#0D1B2A]">
            <CardContent className="p-8 text-center">
              <CheckCircle className="w-12 h-12 mx-auto text-green-400 mb-3" />
              <p className="text-gray-400">No active loads</p>
              <p className="text-xs text-gray-500 mt-1">Check back later for new assignments</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {activeLoads.map((load) => (
              <Card 
                key={load.id}
                className="border-[#3B82F6]/20 bg-[#0D1B2A] hover:border-[#4ADE80]/40 transition-all cursor-pointer"
                onClick={() => navigate(`/driver/load/${load.id}`)}
              >
                <CardContent className="p-4">
                  {/* Status Badge */}
                  <div className="flex items-center justify-between mb-3">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(load.assignment_status)}`}>
                      {getStatusLabel(load.assignment_status)}
                    </span>
                    <span className="text-lg font-bold text-[#4ADE80]">${load.price?.toLocaleString()}</span>
                  </div>
                  
                  {/* Route */}
                  <div className="space-y-2 mb-3">
                    <div className="flex items-start gap-2">
                      <MapPin className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-white font-medium text-sm">{load.pickup_location}</p>
                        <p className="text-xs text-gray-500">{formatDate(load.pickup_date)}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <MapPin className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-white font-medium text-sm">{load.delivery_location}</p>
                        {load.delivery_date && (
                          <p className="text-xs text-gray-500">{formatDate(load.delivery_date)}</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Vehicle Info */}
                  {load.vehicle_info && (
                    <p className="text-xs text-gray-400 mb-3">
                      <Truck className="w-3 h-3 inline mr-1" />
                      {load.vehicle_info}
                    </p>
                  )}

                  {/* Action Button */}
                  <div className="flex items-center justify-between pt-3 border-t border-[#3B82F6]/10">
                    <span className="text-xs text-gray-500">
                      Tap for details & actions
                    </span>
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Completed Loads Section */}
      {completedLoads.length > 0 && (
        <div className="px-4 mt-6">
          <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-3 flex items-center gap-2">
            <CheckCircle className="w-4 h-4" />
            Completed ({completedLoads.length})
          </h2>
          
          <div className="space-y-2">
            {completedLoads.slice(0, 3).map((load) => (
              <Card 
                key={load.id}
                className="border-[#3B82F6]/10 bg-[#0D1B2A]/50 cursor-pointer"
                onClick={() => navigate(`/driver/load/${load.id}`)}
              >
                <CardContent className="p-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-green-400" />
                      <div>
                        <p className="text-sm text-white">
                          {load.pickup_location?.split(',')[0]} → {load.delivery_location?.split(',')[0]}
                        </p>
                        <p className="text-xs text-gray-500">{load.vehicle_info}</p>
                      </div>
                    </div>
                    <span className="text-sm font-bold text-green-400">${load.price?.toLocaleString()}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Bottom Navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-[#0D1B2A] border-t border-[#3B82F6]/20 px-4 py-2 safe-area-inset-bottom">
        <div className="flex items-center justify-around">
          <button className="flex flex-col items-center gap-1 text-[#4ADE80]">
            <Package className="w-5 h-5" />
            <span className="text-xs">Loads</span>
          </button>
          <button 
            onClick={() => navigate('/driver/expenses')}
            className="flex flex-col items-center gap-1 text-gray-400 hover:text-white transition-colors"
          >
            <Receipt className="w-5 h-5" />
            <span className="text-xs">Expenses</span>
          </button>
          <button 
            onClick={() => navigate('/driver/documents')}
            className="flex flex-col items-center gap-1 text-gray-400 hover:text-white transition-colors"
          >
            <FileText className="w-5 h-5" />
            <span className="text-xs">Docs</span>
          </button>
          <button 
            onClick={() => navigate('/driver/profile')}
            className="flex flex-col items-center gap-1 text-gray-400 hover:text-white transition-colors"
          >
            <User className="w-5 h-5" />
            <span className="text-xs">Profile</span>
          </button>
        </div>
      </div>
      
      {/* SOS Emergency Button */}
      <SOSButton loadId={loads.find(l => l.assignment_status === 'in_transit')?.id} />
    </div>
  );
};

export default DriverDashboard;
